import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaEnvelope, FaEdit, FaSave, FaTimes, FaLock } from "react-icons/fa";

// Brand palette, matching the navbar/footer gradient and accent colors
const COLORS = {
  primaryDark: "#234a94",
  primary: "#305DBD",
  periwinkle: "#8FBCFF",
  palest: "#E1E9FC",
  mint: "#3EB489",
  mintLight: "#ADFFC3",
  ink: "#14161B",
  border: "#D4D8DF",
};

export default function Profile({ onBack }) {
  const { user, setUser, updateUserEmail, updateUserName } = useAuth();

  const [loading, setLoading] = useState(true);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");

  const [tempFirstName, setTempFirstName] = useState("");
  const [tempLastName, setTempLastName] = useState("");
  const [tempEmail, setTempEmail] = useState("");

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/get-user?email=${user.email}`);
        const data = await res.json();
        if (res.ok) {
          const updatedUser = {
            user_id: data.user_id,
            firstName: data.first_name,
            lastName: data.last_name,
            email: data.email,
            role: data.role,
          };
          setFirstName(updatedUser.firstName);
          setLastName(updatedUser.lastName);
          setEmail(updatedUser.email);
          setTempFirstName(updatedUser.firstName);
          setTempLastName(updatedUser.lastName);
          setTempEmail(updatedUser.email);
          setUser(updatedUser);
        } else {
          toast.error(data.message || "Failed to fetch user details");
        }
      } catch (err) {
        toast.error("Error fetching user details");
      } finally {
        setLoading(false);
      }
    };
    if (user?.email) fetchUser();
  }, [user?.email]);

  const handleEdit = () => {
    setEditing(true);
    setTempFirstName(firstName);
    setTempLastName(lastName);
    setTempEmail(email);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (tempFirstName !== firstName || tempLastName !== lastName) {
        const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/update-user", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, firstName: tempFirstName, lastName: tempLastName }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to update name");
        updateUserName({ firstName: tempFirstName, lastName: tempLastName });
      }

      if (tempEmail !== email) {
        const res = await fetch("https://dshb-deposits.its.uiowa.edu/api/update-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ oldEmail: email, newEmail: tempEmail }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Failed to update email");
        setEmail(tempEmail);
        updateUserEmail(tempEmail);
      }

      setFirstName(tempFirstName);
      setLastName(tempLastName);
      setEditing(false);
      toast.success("Profile updated successfully!");
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setTempFirstName(firstName);
    setTempLastName(lastName);
    setTempEmail(email);
  };

  const initials = `${firstName?.[0] || ""}${lastName?.[0] || ""}`.toUpperCase();

  if (loading) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", color: "#6b7280" }}>
        <div style={{
          width: 36, height: 36, border: "3px solid #E1E9FC", borderTopColor: COLORS.primary,
          borderRadius: "50%", animation: "profile-spin 0.8s linear infinite", marginBottom: "12px",
        }} />
        <p>Loading profile...</p>
        <style>{`@keyframes profile-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const Field = ({ icon, label, value, editingValue, onChange, type = "text" }) => (
    <div style={{
      display: "flex", flexDirection: "column", gap: "6px",
      padding: "16px 0", borderBottom: "1px solid #f1f5f9",
    }}>
      <label style={{
        display: "flex", alignItems: "center", gap: "8px",
        fontSize: "11px", fontWeight: 700, color: COLORS.primary,
        textTransform: "uppercase", letterSpacing: "0.05em",
      }}>
        {icon} {label}
      </label>
      {editing ? (
        <input
          type={type}
          value={editingValue}
          onChange={onChange}
          style={{
            padding: "9px 12px", border: `1.5px solid ${COLORS.border}`, borderRadius: "8px",
            fontSize: "14px", fontFamily: "inherit", color: COLORS.ink,
            transition: "border-color 0.15s",
          }}
          onFocus={(e) => e.target.style.borderColor = COLORS.primary}
          onBlur={(e) => e.target.style.borderColor = COLORS.border}
        />
      ) : (
        <span style={{ fontSize: "15px", color: COLORS.ink, fontWeight: 500 }}>{value}</span>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
      {/* Toasts render via the single global <ToastContainer> in App.js —
          a local one here used to double-fire every toast with mismatched
          styling (App.js's theme="colored" vs this file's unthemed default). */}

      <div style={{
        width: "100%", maxWidth: "520px", background: "#fff",
        borderRadius: "16px", overflow: "hidden",
        boxShadow: "0 4px 24px rgba(20, 22, 27, 0.10)",
        border: `1px solid ${COLORS.border}`,
      }}>
        {/* Header — quiet periwinkle tint instead of a bold gradient
            banner, so it reads as a section, not a hero. */}
        <div style={{
          background: COLORS.palest,
          borderBottom: `1px solid ${COLORS.border}`,
          padding: "26px 28px",
          display: "flex", alignItems: "center", gap: "16px",
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%",
            background: COLORS.primary, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: "20px", fontWeight: 800, flexShrink: 0,
          }}>
            {initials || <FaUser />}
          </div>
          <div>
            <h1 style={{ margin: "0 0 2px", fontSize: "19px", fontWeight: 800, color: COLORS.ink }}>
              {firstName} {lastName}
            </h1>
            <p style={{ margin: 0, fontSize: "13px", color: COLORS.primary, fontWeight: 600 }}>
              Depositor account
            </p>
          </div>
        </div>

        {/* Fields */}
        <div style={{ padding: "8px 28px 4px" }}>
          <Field
            icon={<FaUser />}
            label="First Name"
            value={firstName}
            editingValue={tempFirstName}
            onChange={(e) => setTempFirstName(e.target.value)}
          />
          <Field
            icon={<FaUser />}
            label="Last Name"
            value={lastName}
            editingValue={tempLastName}
            onChange={(e) => setTempLastName(e.target.value)}
          />
          <Field
            icon={<FaEnvelope />}
            label="Email Address"
            value={email}
            editingValue={tempEmail}
            type="email"
            onChange={(e) => setTempEmail(e.target.value)}
          />
        </div>

        {/* Security note */}
        <div style={{ padding: "16px 28px" }}>
          <div style={{
            display: "flex", gap: "12px", alignItems: "flex-start",
            padding: "14px 16px", background: COLORS.palest, borderRadius: "10px",
            borderLeft: `3px solid ${COLORS.primary}`,
          }}>
            <FaLock style={{ color: COLORS.primary, fontSize: "15px", marginTop: "2px", flexShrink: 0 }} />
            <div>
              <p style={{ margin: "0 0 2px", fontSize: "13px", fontWeight: 700, color: COLORS.primaryDark }}>
                Account Security
              </p>
              <p style={{ margin: 0, fontSize: "12px", color: "#374151", lineHeight: 1.5 }}>
                Changes to your name or email are saved immediately when you click Save Changes.
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: "flex", gap: "10px", padding: "18px 28px",
          borderTop: `1px solid ${COLORS.border}`, background: "#fafbfc",
        }}>
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                  padding: "12px", background: saving ? "#94a3b8" : COLORS.mint, color: "#fff",
                  border: "none", borderRadius: "9px", fontWeight: 700, fontSize: "14px",
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                <FaSave /> {saving ? "Saving…" : "Save Changes"}
              </button>
              <button
                onClick={handleCancel}
                disabled={saving}
                style={{
                  padding: "12px 20px", background: "#fff", color: "#6b7280",
                  border: `1.5px solid ${COLORS.border}`, borderRadius: "9px",
                  fontWeight: 600, fontSize: "14px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: "8px",
                }}
              >
                <FaTimes /> Cancel
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
                padding: "12px", background: `linear-gradient(120deg, ${COLORS.primaryDark}, ${COLORS.primary})`,
                color: "#fff", border: "none", borderRadius: "9px",
                fontWeight: 700, fontSize: "14px", cursor: "pointer",
                boxShadow: "0 2px 8px rgba(48, 93, 189, 0.3)",
              }}
            >
              <FaEdit /> Edit Profile
            </button>
          )}
        </div>
      </div>
    </div>
  );
}